// Dungeon event catalogue: types, starter pools, pick functions, and
// effect-application helpers for ADR 0030 §2.4 (events) and §2.6.2 /
// §2.10 (effect handler scope).
//
// Scope of this file (Step 10c-event-base, first slice of ADR 0030 §2.4):
//   - EventEffect / EventOutcome / TextEventChoice / TextEventDefinition
//     / TreasureEventDefinition types.
//   - STARTER_TEXT_EVENTS (8 entries) and STARTER_TREASURE_EVENTS
//     (5 entries) catalogues, mirroring §2.4.1 / §2.4.2 verbatim.
//   - eventSeed (statistically independent from boonSeed / subtypeSeed /
//     battleSeed / bossEscortSeed) and pick functions.
//   - applyEventEffect: pure DungeonRunState reducer covering the 7
//     EventEffect kinds. The Phase 3 implementation scope follows ADR 0030
//     §2.10 amendment: gold-delta credits character.gold (the caller
//     splices that onto the character record), apply-debuff queues into
//     DungeonRunState.pendingDebuffs without firing the sim BuffStack,
//     and grant-item's rarityHint is stored but not interpreted.
//
// Event UI dispatch (DungeonsPage), grant-boon's pickBoon integration,
// grant-item's generateItem call, and the ambush battle launcher all
// live in the client package — this file exposes the pure pieces they
// compose.
import { Rng } from './rng.js';
// ---------------------------------------------------------------------------
// Starter pools (ADR 0030 §2.4.1 / §2.4.2)
// ---------------------------------------------------------------------------
/** ADR 0030 §2.4.1 — 8 starter text events. */
export const STARTER_TEXT_EVENTS = [
    {
        id: 'event.text.shrine',
        title: '古びた祠',
        description: '苔むした祠が森のなかに佇んでいる。冷えた石の表面に薄く文字が刻まれている。',
        choices: [
            {
                label: '祈る',
                outcomes: [
                    { weight: 1, resultLabel: '祠が淡く光った。祝福を受けた気がする。', effects: [{ kind: 'grant-boon' }] },
                ],
            },
            {
                label: '無視する',
                outcomes: [
                    { weight: 1, resultLabel: '何も起こらない。', effects: [{ kind: 'noop' }] },
                ],
            },
        ],
    },
    {
        id: 'event.text.wanderer',
        title: '流浪の行商',
        description: '隊商から離れたらしい行商人がぽつんと立っている。手持ちの品を一つだけ売ってくれるという。',
        choices: [
            {
                label: '買う (-100g)',
                outcomes: [
                    {
                        weight: 1,
                        resultLabel: 'よい買い物だった。',
                        effects: [{ kind: 'gold-delta', amount: -100 }, { kind: 'grant-item' }],
                    },
                ],
            },
            {
                label: '立ち去る',
                outcomes: [
                    { weight: 1, resultLabel: '行商人は寂しげに見送った。', effects: [{ kind: 'noop' }] },
                ],
            },
        ],
    },
    {
        id: 'event.text.well',
        title: '暗い井戸',
        description: '深い井戸の奥から、何かの気配を感じる。手を伸ばせば届きそうな距離だ。',
        choices: [
            {
                label: '身を乗り出す',
                outcomes: [
                    { weight: 0.5, resultLabel: '清らかな水が湧き上がり、傷を癒した。', effects: [{ kind: 'hp-delta', ratio: 0.3 }] },
                    { weight: 0.5, resultLabel: '不浄な水が傷を悪化させた。', effects: [{ kind: 'hp-delta', ratio: -0.2 }] },
                ],
            },
            {
                label: '離れる',
                outcomes: [
                    { weight: 1, resultLabel: '何事もなかった。', effects: [{ kind: 'noop' }] },
                ],
            },
        ],
    },
    {
        id: 'event.text.statue',
        title: '砕けた偶像',
        description: 'ばらばらに砕けた偶像が散らばっている。直すべきか、いっそ破壊するか。',
        choices: [
            {
                label: '修復する (-150g)',
                outcomes: [
                    {
                        weight: 1,
                        resultLabel: '偶像は柔らかな光を取り戻した。',
                        effects: [
                            { kind: 'gold-delta', amount: -150 },
                            { kind: 'penalty-pct', delta: -0.05 },
                        ],
                    },
                ],
            },
            {
                label: '破壊する',
                outcomes: [
                    {
                        weight: 1,
                        resultLabel: '崩れた中から小銭が転がり出たが、何かの呪いを受けた。',
                        effects: [
                            { kind: 'gold-delta', amount: 80 },
                            { kind: 'apply-debuff', statusId: 'curse', stacks: 2 },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'event.text.beggar',
        title: '物乞いの老婆',
        description: '道のはずれで、痩せた老婆が手のひらを差し出している。',
        choices: [
            {
                label: '施しを与える (-50g)',
                outcomes: [
                    {
                        weight: 1,
                        resultLabel: '老婆は微笑み、古い呪文を授けた。',
                        effects: [{ kind: 'gold-delta', amount: -50 }, { kind: 'grant-boon' }],
                    },
                ],
            },
            {
                label: '無視する',
                outcomes: [
                    {
                        weight: 1,
                        resultLabel: '老婆の呟きが胸に刺さる。後ろめたさが残った。',
                        effects: [{ kind: 'penalty-pct', delta: 0.05 }],
                    },
                ],
            },
        ],
    },
    {
        id: 'event.text.scroll',
        title: '落ちた巻物',
        description: '地面に古びた巻物が落ちている。封蝋は割れている。',
        choices: [
            {
                label: '読む',
                outcomes: [
                    { weight: 0.5, resultLabel: '巻物の知識が体に流れ込んだ。', effects: [{ kind: 'grant-boon' }] },
                    {
                        weight: 0.5,
                        resultLabel: '巻物には呪詛が記されていた。',
                        effects: [{ kind: 'apply-debuff', statusId: 'erosion', stacks: 3 }],
                    },
                ],
            },
            {
                label: '破棄する',
                outcomes: [
                    { weight: 1, resultLabel: '巻物は風に乗って消えた。', effects: [{ kind: 'noop' }] },
                ],
            },
        ],
    },
    {
        id: 'event.text.crossroads',
        title: '道しるべ',
        description: '崩れかけた道しるべが二つの道を示している。どちらに進む？',
        choices: [
            {
                label: '左の近道',
                outcomes: [
                    {
                        weight: 1,
                        resultLabel: '近道で時間は稼いだが、装備が消耗した。',
                        effects: [{ kind: 'penalty-pct', delta: 0.05 }],
                    },
                ],
            },
            {
                label: '右の迂回路',
                outcomes: [
                    { weight: 1, resultLabel: '迂回路で珍しいものを拾った。', effects: [{ kind: 'grant-boon' }] },
                ],
            },
        ],
    },
    {
        id: 'event.text.mirror',
        title: '朽ちた鏡',
        description: '苔と埃に覆われた古い鏡が壁に掛かっている。',
        choices: [
            {
                label: '覗き込む',
                outcomes: [
                    { weight: 1, resultLabel: '鏡の奥から見覚えのない祝福が囁いた。', effects: [{ kind: 'grant-boon' }] },
                ],
            },
            {
                label: '叩き割る',
                outcomes: [
                    {
                        weight: 1,
                        resultLabel: '破片の中から小袋が落ちた。',
                        effects: [{ kind: 'gold-delta', amount: 120 }],
                    },
                ],
            },
        ],
    },
];
/** ADR 0030 §2.4.2 — 5 starter treasure events. */
export const STARTER_TREASURE_EVENTS = [
    {
        id: 'event.treasure.chest',
        title: '古びた宝箱',
        description: '埃を被った木製の宝箱。錠は壊れている。',
        outcomes: [
            { weight: 0.7, resultLabel: '中に道具が入っていた。', effects: [{ kind: 'grant-item' }] },
            { weight: 0.2, resultLabel: '小銭が入っていた。', effects: [{ kind: 'gold-delta', amount: 120 }] },
            { weight: 0.1, resultLabel: '罠が仕込まれていた。', effects: [{ kind: 'hp-delta', ratio: -0.15 }] },
        ],
    },
    {
        id: 'event.treasure.urn',
        title: 'ひびの入った壺',
        description: '陶器の壺が転がっている。中で何かが小さく光っている。',
        outcomes: [
            { weight: 0.6, resultLabel: '中に金貨が入っていた。', effects: [{ kind: 'gold-delta', amount: 80 }] },
            { weight: 0.3, resultLabel: '中に道具が入っていた。', effects: [{ kind: 'grant-item' }] },
            { weight: 0.1, resultLabel: '中は空だった。', effects: [{ kind: 'noop' }] },
        ],
    },
    {
        id: 'event.treasure.altar',
        title: '血錆の祭壇',
        description: '黒く染まった祭壇に、何かを捧げると応えがある気がする。',
        outcomes: [
            { weight: 0.4, resultLabel: '祭壇から祝福が湧き上がった。', effects: [{ kind: 'grant-boon' }] },
            { weight: 0.4, resultLabel: '祭壇の中から道具が現れた。', effects: [{ kind: 'grant-item' }] },
            {
                weight: 0.2,
                resultLabel: '祭壇の血錆が傷を開いた。',
                effects: [
                    { kind: 'hp-delta', ratio: -0.2 },
                    { kind: 'penalty-pct', delta: 0.05 },
                ],
            },
        ],
    },
    {
        id: 'event.treasure.chest-fancy',
        title: '装飾された宝箱',
        description: '黄金の装飾が施された豪奢な宝箱。',
        outcomes: [
            {
                weight: 0.85,
                resultLabel: '高価そうな道具が入っていた。',
                effects: [{ kind: 'grant-item', rarityHint: 'rare' }],
            },
            {
                weight: 0.15,
                resultLabel: '毒の罠だった。',
                effects: [{ kind: 'apply-debuff', statusId: 'poison', stacks: 3 }],
            },
        ],
    },
    {
        id: 'event.treasure.cache',
        title: '隠し倉',
        description: '岩陰に隠された小さな倉。複数の品が雑然と詰まっている。',
        outcomes: [
            { weight: 0.5, resultLabel: '道具を見つけた。', effects: [{ kind: 'grant-item' }] },
            { weight: 0.3, resultLabel: '金貨を見つけた。', effects: [{ kind: 'gold-delta', amount: 150 }] },
            { weight: 0.1, resultLabel: '記された秘伝の祝福を受けた。', effects: [{ kind: 'grant-boon' }] },
            { weight: 0.1, resultLabel: '罠だった。', effects: [{ kind: 'hp-delta', ratio: -0.1 }] },
        ],
    },
];
// ---------------------------------------------------------------------------
// Ambush enemy lineup (ADR 0030 §2.4.3)
// ---------------------------------------------------------------------------
/**
 * Ambush enemy ids the encounter builder picks from. These are sim
 * template ids (`ENEMY_TEMPLATES` in @renavora/sim) — keep them as
 * strings so shared has no reverse dependency on sim.
 *
 * Order matters: index 0 (`bandit-leader`) is the elite-flagged leader
 * paired with mob escorts; indices 1/2 are stand-alone medium-threat
 * variants picked when the leader isn't rolled.
 */
export const AMBUSH_LEADER_IDS = [
    'enemy.ambush.bandit-leader',
    'enemy.ambush.shadow',
    'enemy.ambush.cultist',
];
// ---------------------------------------------------------------------------
// Seed derivation (independent from subtype / battle / boon / escort)
// ---------------------------------------------------------------------------
/**
 * Deterministic event-roll seed for a given event node. Constants chosen
 * so the output is statistically independent of the other seed channels;
 * tests pin pairwise distinctness for matched (mapSeed, coord).
 */
export function eventSeed(mapSeed, coord) {
    const [floor, index] = coord;
    return ((mapSeed ^ Math.imul(floor, 0xcc9e2d51) ^ Math.imul(index, 0x1b873591)) >>> 0);
}
/** Pick one text event deterministically by `(runSeed, coord)`. */
export function pickTextEvent(opts) {
    const pool = opts.pool ?? STARTER_TEXT_EVENTS;
    if (pool.length === 0) {
        throw new Error('pickTextEvent: empty pool');
    }
    const rng = Rng.fromSeed(eventSeed(opts.runSeed, opts.coord));
    return pool[rng.nextInt(pool.length)];
}
/** Pick one treasure-event definition deterministically. */
export function pickTreasureEvent(opts) {
    const pool = opts.pool ?? STARTER_TREASURE_EVENTS;
    if (pool.length === 0) {
        throw new Error('pickTreasureEvent: empty pool');
    }
    const rng = Rng.fromSeed(eventSeed(opts.runSeed, opts.coord));
    return pool[rng.nextInt(pool.length)];
}
/**
 * Roll one outcome from a weighted outcome list. Weights are NOT
 * renormalised — the catalogue is structurally required to sum to 1.0,
 * and any floating-point slack falls through to the last outcome
 * (matches the existing `pickWeighted` in client-side dungeon-subtype).
 */
export function rollOutcome(outcomes, rng) {
    if (outcomes.length === 0) {
        throw new Error('rollOutcome: empty outcome list');
    }
    const roll = rng.nextFloat();
    let acc = 0;
    for (const o of outcomes) {
        acc += o.weight;
        if (roll < acc)
            return o;
    }
    return outcomes[outcomes.length - 1];
}
/**
 * Pick the ambush leader + (implicit) 3 mob escorts. Mob template id is
 * a client-side decision (theme-aware mob pool is follow-up); shared
 * only fixes the leader and the count.
 */
export function pickAmbushEncounter(opts) {
    const pool = opts.pool ?? AMBUSH_LEADER_IDS;
    if (pool.length === 0) {
        throw new Error('pickAmbushEncounter: empty leader pool');
    }
    const rng = Rng.fromSeed(eventSeed(opts.runSeed, opts.coord));
    const leaderId = pool[rng.nextInt(pool.length)];
    return { leaderId, mobCount: 3 };
}
// ---------------------------------------------------------------------------
// EventEffect application (ADR 0030 §2.6.2 / §2.10)
// ---------------------------------------------------------------------------
/**
 * Cumulative reward-penalty cap (ADR 0026 §2.7). Mirrors the constant
 * already enforced by `recordBattleResult`; duplicated here so the
 * event-effect helper stays self-contained.
 */
export const PENALTY_PCT_CAP = 0.5;
/**
 * Default `remainingFights` value pushed onto pendingDebuffs by an
 * `apply-debuff` event effect. ADR §2.6.2 pins "3-5 戦闘で expire"; we
 * pick the midpoint so Phase 4's decrement reducer has a predictable
 * baseline.
 */
export const PENDING_DEBUFF_DEFAULT_FIGHTS = 4;
/**
 * Apply a single EventEffect to the run state, returning the next state
 * plus the off-state side-effect channel (gold delta, boon-pick request,
 * item-grant signal). Pure: no Rng, no character mutation, no I/O.
 *
 * The caller is expected to be the active-character store's event
 * dispatcher (`active-character.ts` event branch, lands in a follow-up
 * PR), which composes the gold/boon/item credits with the returned
 * `run` and persists via `persistDungeonRun`.
 *
 * ADR 0030 §2.10 amendment scope:
 *   - hp-delta: clamps partyHpRatio to [0, 1]. The hero never starts a
 *     battle below 1 HP (`buildBattleFromNode` enforces a floor of 1) so
 *     0 here means "dead party — defeat path on next battle".
 *   - gold-delta: returns `goldDelta` for the caller to credit to
 *     character.gold. We do not floor at 0 here; the caller decides
 *     whether to clamp (event purchases that cost more than the player
 *     has are catalogue-level concerns).
 *   - grant-boon: pure signal — caller calls pickBoon and pushes.
 *   - grant-item: pure signal — caller calls generateItem and pushes.
 *     rarityHint is forwarded but not interpreted.
 *   - apply-debuff: appends to pendingDebuffs with remainingFights = 4.
 *   - penalty-pct: clamps to [0, PENALTY_PCT_CAP].
 *   - noop: returns the run unchanged.
 */
export function applyEventEffect(run, effect) {
    switch (effect.kind) {
        case 'hp-delta': {
            const next = clamp(run.partyHpRatio + effect.ratio, 0, 1);
            const sign = effect.ratio >= 0 ? '+' : '';
            return {
                run: next === run.partyHpRatio ? run : { ...run, partyHpRatio: next },
                goldDelta: 0,
                grantsBoon: false,
                itemRarityHint: null,
                displayLabel: `HP ${sign}${Math.round(effect.ratio * 100)}%`,
            };
        }
        case 'gold-delta': {
            const sign = effect.amount >= 0 ? '+' : '';
            return {
                run,
                goldDelta: effect.amount,
                grantsBoon: false,
                itemRarityHint: null,
                displayLabel: `Gold ${sign}${effect.amount}`,
            };
        }
        case 'grant-boon':
            return {
                run,
                goldDelta: 0,
                grantsBoon: true,
                itemRarityHint: null,
                displayLabel: 'Boon granted',
            };
        case 'grant-item':
            return {
                run,
                goldDelta: 0,
                grantsBoon: false,
                itemRarityHint: effect.rarityHint ?? null,
                displayLabel: effect.rarityHint !== undefined
                    ? `Item (${effect.rarityHint})`
                    : 'Item',
            };
        case 'apply-debuff': {
            const pending = {
                statusId: effect.statusId,
                stacks: effect.stacks,
                remainingFights: PENDING_DEBUFF_DEFAULT_FIGHTS,
            };
            const prior = run.pendingDebuffs ?? [];
            return {
                run: { ...run, pendingDebuffs: [...prior, pending] },
                goldDelta: 0,
                grantsBoon: false,
                itemRarityHint: null,
                displayLabel: `${effect.statusId} x${effect.stacks}`,
            };
        }
        case 'penalty-pct': {
            const next = clamp(run.penaltyPct + effect.delta, 0, PENALTY_PCT_CAP);
            const sign = effect.delta >= 0 ? '+' : '';
            return {
                run: next === run.penaltyPct ? run : { ...run, penaltyPct: next },
                goldDelta: 0,
                grantsBoon: false,
                itemRarityHint: null,
                displayLabel: `Penalty ${sign}${Math.round(effect.delta * 100)}%`,
            };
        }
        case 'noop':
            return {
                run,
                goldDelta: 0,
                grantsBoon: false,
                itemRarityHint: null,
                displayLabel: '—',
            };
    }
}
function clamp(value, lo, hi) {
    if (value < lo)
        return lo;
    if (value > hi)
        return hi;
    return value;
}
//# sourceMappingURL=dungeon-events.js.map